import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { unwrapResult } from '@reduxjs/toolkit';
import dayjs from 'dayjs';

import { PEARL, PRIMARY, RED, TEXT } from '../../../../../shared/Helpers/colors';
import { CustomAlert, CustomButton, Text } from '../../../../../shared/uiComponents';
import { errorNormalizer } from '../../../../../shared/Helpers/functions';
import { InfoFilled } from '../../../../../shared/Assets/svgIcons';
import { Play, Stop, TrashEmpty } from '../../../../../shared/Assets/sessionDetailsSVGs';

import { DispatchProperties, useSelector } from '../../../../../redux/store';
import { deleteDuration, getDurations, saveDuration } from '../../../../../redux/State/clientSlice/dataCollectionSlice';
import { BehaviorReductionProperties } from '../../../../../redux/API/ClientAPIHelpers/behaviorReductionProperties';
import { DurationProperties } from '../../../../../redux/API/ClientAPIHelpers/dataCollectionProperties';

const DurationItem = ({ duration, behaviorId }: { behaviorId: string; duration: DurationProperties }) => {
	const dispatch = useDispatch<DispatchProperties>();
	const { sessionId } = useParams();

	const deleteHandler = () => {
		if (!sessionId) return;
		dispatch(deleteDuration({ sessionId, behaviorId, recordId: duration.id }))
			.then(unwrapResult)
			.then(() => {
				dispatch(getDurations({ behaviorId, sessionId }));
			})
			.catch(errorNormalizer);
	};

	return (
		<div className="durationItem">
			<Text title={duration.duration} type="psm" fontWeight="medium" />
			<Text
				title={dayjs(duration.createdAt).format('MM/DD/YYYY hh:mm A')}
				type="psm"
				fontWeight="medium"
				color={TEXT.SECONDARY}
			/>
			<CustomButton
				onClick={deleteHandler}
				buttonType="secondaryGray"
				iconOnly={<TrashEmpty color={RED.BASE} />}
				style={{ backgroundColor: RED.QUATERNARY }}
			/>
		</div>
	);
};

const Content = ({ behavior }: { behavior: BehaviorReductionProperties }) => {
	const dispatch = useDispatch<DispatchProperties>();
	const { sessionId } = useParams();
	const durationList = useSelector((state) => state.dataCollection.duratoionList);

	useEffect(() => {
		if (!behavior.id || !sessionId) return;
		dispatch(getDurations({ behaviorId: behavior.id, sessionId }));
	}, [behavior.id, sessionId, dispatch]);

	return (
		<div className="durationContent">
			{Boolean(behavior.definition) && <Text title={behavior.definition} type="plg" fontWeight="medium" />}
			<div className="durationList">
				{durationList.map((duration) => (
					<DurationItem key={duration.id} duration={duration} behaviorId={behavior.id} />
				))}
				{!durationList.length && <Text title="No duration records" type="psm" color={TEXT.SECONDARY} />}
			</div>
		</div>
	);
};

const DurationType = ({ behavior }: { behavior: BehaviorReductionProperties }) => {
	const { sessionId } = useParams();
	const dispatch = useDispatch<DispatchProperties>();

	const [seconds, setSeconds] = useState<number>(0);
	const [start, setStart] = useState<boolean>(false);
	const [openInfo, setOpenInfo] = useState<boolean>(false);

	const calculateTime = () => {
		const hour = Math.floor(seconds / 3600);
		const minute = Math.floor((seconds % 3600) / 60);
		const second = (seconds % 3600) % 60;

		return `${hour}:${minute}:${second}`;
	};

	useEffect(() => {
		if (!start || !sessionId) return;

		const interval = setInterval(() => {
			setSeconds((prev) => prev + 1);
		}, 1000);

		// eslint-disable-next-line consistent-return
		return () => clearInterval(interval);
	}, [start, dispatch, sessionId, behavior.id]);

	const durationHandler = () => {
		if (!sessionId) return;
		setStart(!start);
		if (!seconds) return;
		dispatch(saveDuration({ behaviorId: behavior.id, sessionId, seconds }))
			.then(unwrapResult)
			.then(() => {
				setSeconds(0);
			})
			.catch(errorNormalizer);
	};

	return (
		<>
			<div className="behaviorTitle">
				{Boolean(behavior.definition) && (
					<div className="infoIcon" onClick={() => setOpenInfo(true)}>
						<InfoFilled color={PRIMARY.BASE} />
					</div>
				)}
				<Text title={behavior.name} style={{ whiteSpace: 'nowrap' }} />
			</div>
			<div className="behaviorAction">
				<CustomButton
					onClick={durationHandler}
					iconOnly={start ? <Stop color={PEARL.BASE[800]} /> : <Play color={PEARL.BASE[800]} />}
					buttonType="tertiary"
				/>
				<Text title={calculateTime()} type="plg" fontWeight="semiBold" color={TEXT.SECONDARY} />
			</div>

			<CustomAlert
				title=""
				open={openInfo}
				onClose={() => setOpenInfo(false)}
				Content={() => <Content behavior={behavior} />}
			/>
		</>
	);
};

export default DurationType;
